import { useMemo } from 'react'
import { useQuery, gql } from '@apollo/client'

import {
    SYSTEM_EXCLUDE_VALUE,
    filterRows,
    rankKeys,
    roleFor,
    toOptions,
    totalsBy,
    totalsByMulti,
} from './activityReport'
import { actionMetaFor, getReport } from './reportRegistry'

export const ACTIVITY_REPORT = gql`query ActivityReport($report: String!, $dateAddedGte: Date!, $dateAddedLte: Date!) {
    activityReport(report: $report, dateAddedGte: $dateAddedGte, dateAddedLte: $dateAddedLte) {
        userId
        userName
        username
        branchName
        scopeNames
        direction
        action
        eventDate
        objectsCount
        eventsCount
        objectIds
    }
}`

/**
 * Each report's per-viewer scope, keyed by id.
 *
 * Deliberately not `hasPermissions`: that field returns only the codenames the
 * caller asked for, so a component that forgets one gets a silent false negative
 * and its check becomes dead code. Asking the server for the catalog it owns
 * removes the chance to forget.
 *
 * Only `id` and `canViewAllUsers` -- everything else this response could carry
 * (name, subtitle, ...) comes from the local `reportRegistry.js` entry
 * instead, which every caller already has via `getReport(reportId)`.
 *
 * `currentUser { pk }` rides along on the same query rather than a separate
 * one: nothing here needs it to resolve any faster or slower than the catalog
 * itself, and the Investigate tab (the one consumer) already waits on
 * `catalogLoading` for `canViewAllUsers`. `pk` (an Int, resolved off
 * `self.id` by `UserNode.resolve_pk`), not the Relay `id`, so it round-trips
 * directly into `activityLogs`' `userId: Int` variable with no decoding.
 */
export const REPORT_CATALOG = gql`query ReportCatalog {
    reportCatalog {
        id
        canViewAllUsers
    }
    currentUser {
        pk
    }
}`

/**
 * Groups and their members, for the group filter.
 *
 * Uses userGroupMemberships rather than allGroups { userSet }: userSet is a
 * nested connection re-evaluated per group at two queries each, which fired a
 * hundred queries on a few dozen groups. The flat field is two queries however
 * many groups exist.
 *
 * Members are matched to report rows by id, never by name: display names are not
 * unique, and the report's name formula and `UserNode.resolve_name` disagree for
 * a user who has a last name but no first name.
 */
export const ACTIVITY_REPORT_GROUPS = gql`query ActivityReportGroups {
    userGroupMemberships {
        id
        name
        userIds
    }
}`

/**
 * Resolves the display name and role for one row's three possible actor
 * populations (see the docstring in `activity/query.py`), so every
 * downstream consumer -- charts, tables, filters, the heatmap -- can treat
 * `userName` as always a non-empty string and never has to special-case null
 * again, and can treat `role` as always one of `roleFor`'s three values.
 *
 * `roleFor` runs on the raw row, before the name is filled in: it keys off
 * `userName === null` meaning "no match", which display-name resolution
 * would otherwise erase.
 *
 * - `userId === null`: no actor at all (cron, signals, migrations,
 *   integrations) -- labelled "No Actor", rolled up under the System role.
 * - `userId` set but `userName` missing: the id matched no `auth_user` row --
 *   `buggy/middleware.py` logs `request.user.id` into history rows whenever
 *   `request.user` is authenticated, and on `/mobileql` that principal is a
 *   `MobileAppDriver`, whose id is a `cars_driver` pk, not an `auth_user` pk.
 *   These are customers, not staff. Labelled "Customer #<id>", never
 *   "No Actor": this is real, attributable activity, just not a name we have
 *   on file (resolving it to an actual customer name would mean joining
 *   `cars_driver`, which this does not do).
 * - otherwise: the name BigQuery resolved, unchanged -- rolled up under the
 *   System role instead of Reps when its username is a listed service
 *   account (see `SYSTEM_USERNAMES` in reportRegistry.js), unchanged
 *   otherwise.
 */
const resolveUserName = (row) => {
    const role = roleFor(row)
    if (row.userId === null) return { ...row, role, userName: 'No Actor' }
    if (!row.userName) return { ...row, role, userName: `Customer #${row.userId}` }
    return { ...row, role }
}

/**
 * Fetches one report for a date range and derives its filter options.
 *
 * The action filter is applied by the caller, not here: it is a display concern
 * and filtering client-side keeps the response instant.
 */
const useActivityReport = ({ reportId, startDate, endDate, filters }) => {
    const { data, loading, error } = useQuery(ACTIVITY_REPORT, {
        variables: { report: reportId, dateAddedGte: startDate, dateAddedLte: endDate },
        skip: !reportId,
    })

    // Neither of these depends on the date range or the report, so they run once
    // and are served from the Apollo cache thereafter.
    const { data: catalogData, loading: catalogLoading } = useQuery(REPORT_CATALOG)
    const { data: groupData } = useQuery(ACTIVITY_REPORT_GROUPS)

    return useMemo(() => {
        const rows = (data?.activityReport || []).map(resolveUserName)
        const catalog = catalogData?.reportCatalog || []
        const actionMeta = actionMetaFor(getReport(reportId))

        const groupOptions = (groupData?.userGroupMemberships || [])
            .map((group) => ({
                value: group.id,
                label: group.name,
                userIds: (group.userIds || []).map(String),
            }))
            // A group with no members would silently filter everything away, so
            // it is not offered as a choice.
            .filter((group) => group.userIds.length)

        // Options come from the *unfiltered* rows, so narrowing a filter never
        // empties the dropdown you are filtering with.
        const userOptions = toOptions(rankKeys(totalsBy(rows, (row) => row.userName, actionMeta)))
        const branchOptions = toOptions(rankKeys(totalsBy(rows, (row) => row.branchName, actionMeta)))
        // `scopeNames` is `[]` on every row for a report with no second
        // dimension (see `scopeLabelFor` in reportRegistry.js) -- rows with
        // nothing in it drop out on their own here (`totalsByMulti` skips an
        // empty list), even though a report without one never offers this
        // filter in the first place (`ReportsPanel`'s `availableKinds` gates
        // on the same `scopeLabelFor`), just so this option list is never
        // wrong on its own terms.
        //
        // `totalsByMulti`, not `totalsBy`: `scopeNames` is a list -- a row can
        // carry more than one value (a message routed into several inboxes) --
        // and every value it carries has to be offered and ranked as its own
        // option, not just the first.
        const scopeOptions = toOptions(rankKeys(totalsByMulti(rows, (row) => row.scopeNames, actionMeta)))

        // The exclude-users filter offers the same people as `userOptions`,
        // plus a "No Actor" entry for rows with no actor at all (`userId ===
        // null`), but its option values are ids, not names: exclusion has to
        // match `filterRows`'s `excludeUserIds`, which is id-based because
        // display names are not unique (see `filterRows`'s docstring). One
        // option per distinct `userId`, so two people sharing a display name
        // still get two separate, individually-excludable entries. No Actor
        // has no real id, so it is keyed by `SYSTEM_EXCLUDE_VALUE`, the
        // sentinel `filterRows` treats a null userId as equivalent to --
        // ranked alongside everyone else by the same volume this report
        // actually saw. The label is not special-cased here: `row.userName`
        // is already "No Actor" for that row by the time this runs (see
        // `resolveUserName` above), so it just follows along automatically.
        const excludeUserKey = (row) => (row.userId === null ? SYSTEM_EXCLUDE_VALUE : row.userId)
        const excludeUserLabelByKey = {}
        rows.forEach((row) => {
            const key = excludeUserKey(row)
            if (!(key in excludeUserLabelByKey)) {
                excludeUserLabelByKey[key] = row.userName
            }
        })
        const excludeUserOptions = rankKeys(totalsBy(rows, excludeUserKey, actionMeta)).map((key) => ({
            value: String(key),
            label: excludeUserLabelByKey[key],
        }))

        return {
            loading,
            error,
            rows,
            filteredRows: filterRows(rows, filters),
            userOptions,
            branchOptions,
            scopeOptions,
            groupOptions,
            excludeUserOptions,
            catalog,
            // Defaults to false, not true: while the catalog is still loading this
            // is a permission-derived flag with no answer yet, and a self-scoped
            // viewer briefly seeing (then losing) the full multi-user layout is a
            // worse transition than the reverse. Callers that need to distinguish
            // "no" from "not answered yet" should check `catalogLoading` too.
            canViewAllUsers: catalog.find((entry) => entry.id === reportId)?.canViewAllUsers ?? false,
            // Exposed separately from `loading`, which reflects only the
            // ACTIVITY_REPORT query: a consumer choosing the self-scoped vs
            // full layout needs to know the catalog itself is still in flight,
            // rather than trusting the `false` default above.
            catalogLoading,
            // The Investigate tab's own account, as a plain Number for
            // comparing against a row's `userId` -- `null` both while the
            // catalog is still loading and if there genuinely is no
            // authenticated user, so a consumer must not tell those two
            // cases apart from this field alone (use `catalogLoading` for
            // that, the same way `canViewAllUsers` already does).
            currentUserId: catalogData?.currentUser?.pk ?? null,
        }
    }, [data, catalogData, catalogLoading, groupData, loading, error, filters, reportId])
}

export default useActivityReport
